import { z } from 'zod';
import type { Brand } from '../brand';

/**
 * SIMULATION randomness — **public by design**.
 *
 * A simulation seed is handed to the browser so the client can render the same
 * hunt the server will later revalidate. Because the player can read it, a
 * simulation seed must only ever drive outcomes that carry **no direct economic
 * value**: mob movement, spawn order, visual effects, the boss timer.
 *
 * Predicting a simulation outcome is harmless — the player already knows it.
 * Predicting an *economic* outcome is not. That is why {@link SimulationSeed}
 * is a distinct nominal type from anything in `./economic`: the two can never
 * be passed to each other's functions without a deliberate, greppable cast.
 *
 * @see ./economic for gacha, rare drops and anything Cash-linked.
 */
export type SimulationSeed = Brand<bigint, 'SimulationSeed'>;

/** Simulation seeds are 64-bit, matching the SplitMix64 state that consumes them. */
export const SIMULATION_SEED_MAX = (1n << 64n) - 1n;

export function simulationSeed(value: bigint): SimulationSeed {
  if (value < 0n || value > SIMULATION_SEED_MAX) {
    throw new RangeError(`simulation seed must be a uint64, received ${value}`);
  }
  return value as SimulationSeed;
}

/** Wire format: a decimal string, like every other large integer we transport. */
export const simulationSeedSchema = z
  .string()
  .regex(/^(0|[1-9]\d{0,19})$/, 'simulation seed must be a uint64 decimal string')
  .transform((value) => simulationSeed(BigInt(value)));

/**
 * The complete list of purposes a simulation seed may serve.
 *
 * This is an allow-list, not documentation: `deriveSimulationSeed` only accepts
 * these labels, so a future `'gacha'` or `'rare-drop'` stream cannot be derived
 * from a client-visible seed by accident.
 */
export const SIMULATION_STREAMS = [
  'mob-spawn',
  'mob-movement',
  'mob-order',
  'boss-timer',
  'boss-scale',
  /**
   * Which enemy each of a pet's cannons shoots at.
   *
   * SIMULATION randomness, and rightly so: choosing a mob is not an economic
   * draw, it decides no reward, and the server has to be able to reproduce it
   * exactly when it re-simulates a window. It is a separate stream so a pet
   * that fires more often cannot shift when the next wave spawns.
   */
  'pet-target',
  /**
   * The phases of the ship's wander across the arena.
   *
   * SIMULATION randomness, and only just: it is drawn once per run, decides no
   * reward and settles nothing - but the ship's position feeds the muzzles, so
   * the server has to arrive at the same path when it re-simulates a window.
   * A separate stream so the ship's route cannot move a spawn or a pet's
   * choice of target.
   */
  'ship-drift',
  'starfield',
  'visual-effect',
  /**
   * Which rune appears on a duel pad when it comes back.
   *
   * SIMULATION randomness, and it has to be. A duel pays nothing - `gold_pot`
   * is refused before a room is ever opened - so no rune decides a reward, and
   * the room must be able to rebuild the exact same match from a snapshot after
   * a hibernation. An economic draw could do neither: its entropy never leaves
   * the server as state, so a restored room would hand out different runes.
   *
   * A separate stream, so a duel respawning pads cannot shift a hunt's spawns
   * and the sequence a room draws is a property of that room alone.
   */
  /* Os fluxos de proc de saída, um por participante (gate V28G.F). */
  'pvp-jewel-outgoing-proc-v1',
  'hunt-jewel-outgoing-proc-v1',
  /*
   * Os fluxos da rolagem POR SALVA do Núcleo Atômico (gate V28G.J).
   *
   * Separados dos de proc, e não uma reutilização deles: a Bomba sorteia uma vez
   * por SALVA e o Ciclone uma vez por PROJÉTIL, portanto uma salva de vinte e
   * oito tiros consome uma rolagem de um destes e vinte e oito do outro. Partilhar
   * o gerador faria cada salva deslocar a sequência de procs, e um duelo com a
   * mesma semente deixaria de ser o mesmo duelo por causa de uma joia que o casco
   * nem tem.
   */
  'pvp-jewel-volley-bomb-v1',
  'hunt-jewel-volley-bomb-v1',
  /*
   * Os fluxos de SELEÇÃO DE ALVO do Arsenal Ígneo (gate V28G.L).
   *
   * SIMULAÇÃO, e tem de ser: escolher que inimigo um míssil persegue não é um
   * sorteio económico, não decide recompensa nenhuma, e o servidor tem de chegar
   * exatamente ao mesmo alvo quando re-simula uma janela ou restaura uma sala.
   *
   * Separados dos dois de cima porque a FINALIDADE é outra e a cadência também:
   * a rolagem por salva acontece em todas as salvas, com joia ou sem ela, e a
   * escolha de alvo só acontece quando um míssil nasce ou perde o alvo. Partilhar
   * o gerador faria uma perseguição deslocar a sequência de procs - e um casco
   * que trocasse de alvo mudaria o proc do tiro seguinte.
   *
   * A rolagem por salva do míssil NÃO tem fluxo próprio, e é deliberado: ela é a
   * mesma rolagem da Bomba, comparada contra intervalos disjuntos do mesmo
   * espaço. Um segundo fluxo seria um segundo sorteio independente, que é
   * exatamente o que o contrato proíbe.
   */
  'pvp-jewel-missile-target-v1',
  'hunt-jewel-missile-target-v1',
  /*
   * Os fluxos do Orbe da Víbora (gate V28G.M).
   *
   * SIMULAÇÃO, e tem de ser: um stack de veneno não decide recompensa nenhuma,
   * e o servidor tem de chegar exatamente aos mesmos stacks quando re-simula uma
   * janela ou restaura uma sala. Uma entropia económica não conseguiria nem uma
   * coisa nem outra - ela nunca sai do servidor como estado.
   *
   * Separados dos quatro de cima porque a FINALIDADE é outra e a UNIDADE
   * também: a rolagem do veneno acontece uma vez por ACERTO DIRETO confirmado, e
   * a loteria ofensiva uma vez por projétil NASCIDO. São instantes diferentes e
   * contagens diferentes - uma salva de vinte e oito tiros que acerta em três
   * consome vinte e oito de um e três do outro - e partilhar o gerador faria
   * cada acerto deslocar a sequência de procs de todos os tiros seguintes.
   *
   * E o veneno tem fluxo PRÓPRIO em vez de partilhar a rolagem da loteria
   * ofensiva, ao contrário do que o míssil faz com a Bomba: o míssil partilha a
   * rolagem porque ele COMPETE com a Bomba - no máximo um dos dois por salva. O
   * veneno não compete com nada: ele é estado, não é um golpe, e o contrato diz
   * que pode coexistir com um Ciclone no mesmo acerto. Duas coisas que podem
   * acontecer ao mesmo tempo precisam de dois sorteios.
   */
  'pvp-jewel-poison-v1',
  'hunt-jewel-poison-v1',
  /*
   * Os fluxos da Garra Hemorrágica (gate V28G.N).
   *
   * SIMULAÇÃO, e tem de ser, pela mesma razão exata que os do veneno: uma marca
   * de sangramento não decide recompensa nenhuma, e o servidor tem de chegar
   * exatamente às mesmas marcas quando re-simula uma janela ou restaura uma
   * sala. Uma entropia económica não conseguiria nem uma coisa nem outra.
   *
   * Separados dos do veneno, e não um intervalo dentro deles, porque as duas
   * joias podem produzir no MESMO acerto direto: uma marca e um stack não
   * competem, e duas coisas que podem acontecer ao mesmo tempo precisam de dois
   * sorteios. Partilhar o gerador faria uma corrida com a Garra ter outra
   * sequência de veneno que uma sem ela, e a diferença não seria da joia.
   *
   * A unidade é a mesma do veneno - uma rolagem por ACERTO DIRETO confirmado - e
   * é isso que os torna dois fluxos com a mesma cadência e valores diferentes.
   */
  'pvp-jewel-bleeding-v1',
  'hunt-jewel-bleeding-v1',
  /*
   * O fluxo da Égide Espinhosa (gate V28G.O).
   *
   * SIMULAÇÃO, e tem de ser, pela mesma razão exata que os cinco de cima: uma
   * reflexão não decide recompensa nenhuma, e o servidor tem de chegar
   * exatamente às mesmas interceções quando restaura uma sala.
   *
   * ## Porque é o primeiro fluxo DEFENSIVO, e o que isso muda
   *
   * Os cinco anteriores são todos do lado de quem ataca: a cadência deles é a
   * cadência do canhão do próprio jogador. Este avança uma vez por instância
   * hostil ELEGÍVEL que chega ao casco, portanto quem o faz andar é o
   * adversário. Partilhá-lo com qualquer um dos outros faria a sequência de
   * runas, procs, veneno e sangramento de um jogador depender de quantas vezes
   * ele foi atingido - e duas salas com a mesma semente deixariam de ser a mesma
   * sala por causa de uma joia que ele nem tem equipada.
   *
   * ## E porque não há gémeo de caça
   *
   * Porque o gate V28G.O é só de PvP, por decisão do dono. A caça tem uma única
   * fonte de dano à nave - um mob que atravessa a fronteira de baixo e é
   * removido no mesmo tique - e o que fazer com ela é a decisão D-4.4, que
   * continua pendente. Um rótulo declarado sem nada que o consuma seria uma
   * promessa que ninguém cumpre.
   */
  'pvp-jewel-reflection-v1',
  /*
   * Os fluxos do Diamante Serrilhado (gate V28G.P).
   *
   * SIMULAÇÃO, e tem de ser, pela mesma razão exata que os seis de cima: um
   * contacto de serra não decide recompensa nenhuma, e o servidor tem de chegar
   * exatamente aos mesmos procs quando re-simula uma janela ou restaura uma
   * sala.
   *
   * ## O que os torna diferentes de todos os anteriores
   *
   * A UNIDADE. Os seis anteriores avançam com um acontecimento de COMBATE - um
   * tiro nascido, um acerto confirmado, uma instância hostil recebida. Este
   * avança com o RELÓGIO: enquanto uma lâmina estiver a tocar um inimigo, ele
   * consome três valores a cada intervalo de contacto, tenha o jogador disparado
   * ou não.
   *
   * Partilhá-lo com qualquer um dos outros faria a sequência de procs, veneno,
   * sangramento e runas de um jogador depender de quanto tempo as serras dele
   * estiveram encostadas a alguém - e duas salas com a mesma semente deixariam
   * de ser a mesma sala por causa de uma joia que nem toda a gente tem.
   *
   * ## E porque a serra própria não sorteia nada
   *
   * Porque ela não tem chance. A quantidade, a velocidade, o intervalo e o dano
   * saem todos do nível por aritmética fechada. Estes três valores por contacto
   * são do Ciclone, do Veneno e da Garra - os procs que o contrato V28G.P deixa
   * um contacto confirmado semear.
   */
  'pvp-jewel-saw-v1',
  'hunt-jewel-saw-v1',
  /**
   * **Os fluxos do Coração Vital** (gate V28G.U).
   *
   * ## O que os torna diferentes dos sete anteriores
   *
   * O LADO. Os sete anteriores sorteiam do lado de quem ataca ou de quem defende
   * - o que este tiro produz, o que este acerto semeia, se este golpe é
   * intercetado. Este sorteia do lado de quem AJUDA, e a pergunta dele - "esta
   * salva também curou alguém?" - não compete com nenhuma das outras.
   *
   * É essa independência que obriga a um fluxo próprio em vez de a um intervalo
   * disjunto: uma salva pode legitimamente lançar um míssil E curar um aliado, e
   * dois acontecimentos que podem ser simultâneos precisam de dois sorteios.
   *
   * Partilhar o fluxo por salva - `*-jewel-volley-bomb-v1` - faria cada salva
   * deslocar a sequência da Bomba e do Míssil, e duas salas com a mesma semente
   * deixariam de ser a mesma sala por causa de uma joia que nem toda a gente tem.
   * O contrato do gate proíbe-o por escrito.
   */
  'pvp-jewel-vital-heart-v1',
  'hunt-jewel-vital-heart-v1',
  /**
   * **Os oito fluxos do mundo aberto** (gate V28I.B.1).
   *
   * SIMULAÇÃO, e tem de ser, pela mesma razão exata que os do duelo: nenhum
   * deles decide recompensa nenhuma - um quadrante não paga nada - e a sala tem
   * de chegar exatamente aos mesmos procs quando é reconstruída de uma
   * instantânea depois de uma eviction.
   *
   * ## Porque oito rótulos NOVOS e não os do duelo
   *
   * Porque um rótulo é o que separa duas sequências. Reutilizar
   * `pvp-jewel-poison-v1` faria a semente privada de uma sala e a de um duelo
   * produzirem o mesmo fluxo se por acaso coincidissem, e - mais importante -
   * faria "de que modo é este fluxo" deixar de ser legível numa linha de código.
   * As finalidades são as mesmas oito; os modos são dois.
   *
   * A cadência, a unidade e as razões pelas quais cada um é separado dos outros
   * são exatamente as documentadas acima para os gémeos de PvP, e não são
   * repetidas aqui: o que muda é o modo, e mais nada.
   *
   * O da REFLEXÃO tem gémeo aqui e não tem na caça, porque um quadrante é o
   * segundo sítio do jogo onde um casco leva com o payload de outro casco.
   */
  'open-world-jewel-outgoing-proc-v1',
  'open-world-jewel-volley-bomb-v1',
  'open-world-jewel-missile-target-v1',
  'open-world-jewel-poison-v1',
  'open-world-jewel-bleeding-v1',
  'open-world-jewel-reflection-v1',
  'open-world-jewel-saw-v1',
  'open-world-jewel-vital-heart-v1',
  /**
   * **Quando um boss final volta a usar o golpe de pressão dele.**
   *
   * Uma finalidade própria, e não uma das oito das joias: a sequência de um
   * gerador é observada pelo saque seguinte, portanto partilhá-lo faria a
   * cadência de um boss depender de quantas joias os pilotos trazem — e duas
   * salas com a mesma semente deixariam de ser a mesma sala por causa disso.
   */
  'open-world-boss-pressure-v1',
  'pvp-rune',
] as const;

export type SimulationStream = (typeof SIMULATION_STREAMS)[number];

export const simulationStreamSchema = z.enum(SIMULATION_STREAMS);

export function isSimulationStream(value: string): value is SimulationStream {
  return (SIMULATION_STREAMS as readonly string[]).includes(value);
}
